<template>
    <main :class="$style.page">
        <MyGamesBlock :gameLinkTarget="undefined" />
        <template
            v-for="(category, index) in preparedCategories"
            :key="category.id"
        >
            <LazyHydration :hydrate="hydrateOnIdle()">
                <GamesBlock
                    :title="category.title"
                    :categoryId="category.id"
                    :link="category.location"
                    :games="category.games"
                    :eagerThumbs="index < 1 ? topEagerThumbsCount : 0"
                    :gameLinkTarget="undefined"
                    :preloadFirstCard="index === 0"
                />
            </LazyHydration>

            <LazyHydration :hydrate="hydrateOnIdle()">
                <PromoCategories v-if="index === PROMO_INDEX" />
            </LazyHydration>
        </template>
        <SeoText
            v-if="!appStore.isAppEnv"
            :content="$tr('homePage.seo')"
            :class="$style.seo_override"
        />
        <TheEnd />
    </main>
</template>
<script lang="ts" setup>
import { useHead } from '@unhead/vue'
import { storeToRefs } from 'pinia'
import { computed, hydrateOnIdle, onServerPrefetch } from 'vue'

import PlaygamaOG from '@/assets/icons/Playgama-OG.webp'
import LazyHydration from '@/components/LazyHydration.vue'
import PromoCategories from '@/components/PromoCategories/PromoCategories.vue'
import SeoText from '@/components/SeoText/SeoText.vue'
import TheEnd from '@/components/TheEnd/TheEnd.vue'
import { useActiveNSec } from '@/composables/use-active-n-sec'
import { useSchemaOrg } from '@/composables/use-schema-org'
import { useTr } from '@/composables/use-tr'
import { useGeneralData } from '@/query/use-general-data'
import { ROUTE_NAMES } from '@/router/constants'
import { useAppStore } from '@/store/app-store'
import { useLoggerStore } from '@/store/logger'
import type { MainCategoriesType } from '@/types'
import { unzipShortGame } from '@/utils/helpers'

import GamesBlock from './components/GamesBlock/GamesBlock.vue'
import MyGamesBlock from './components/MyGamesBlock/MyGamesBlock.vue'
import { type MainCategoryListZipType, useHomepageData } from './use-homepage-data'

const appStore = useAppStore()
const loggerStore = useLoggerStore()
const { mainData } = useGeneralData()
const contentCategoriesList = computed(() => mainData.value.contentCategoriesList || [])
const { Organization, WebSite, WebSiteId, getFullLink, getInLanguage } = useSchemaOrg()
const tr = useTr()
const { suspense, categoriesWithGames, highlightedHomepageCategories } = useHomepageData()

const { platform } = storeToRefs(appStore)

const PROMO_INDEX = 3

const topEagerThumbsCount = computed(() => (platform.value === 'desktop' ? 4 : 5))

const placeholderCategories = computed((): MainCategoryListZipType[] => {
    return contentCategoriesList.value.length > 0
        ? contentCategoriesList.value.map((category) => ({
              // map contentCategoriesList to ensure that no "games" field is present so carousel will be dummy
              id: category.id,
              title: category.title,
              location: category.location,
          }))
        : [...highlightedHomepageCategories, ...[1, 2, 3].map((i) => `placeholder-${i}`)].map((c) => ({
              id: c as MainCategoriesType,
              title: '\u00A0', // non-breaking space to preserve title space
              location: undefined,
          }))
})

const preparedCategories = computed(() => {
    const categories = (categoriesWithGames.value || placeholderCategories.value).filter(
        ({ id }) => !(appStore.isAppEnv && id === 'random'),
    ) //filter out 'random' category for apps

    const MAX_GAME_IN_CATEGORY = 20

    return [
        ...highlightedHomepageCategories
            .map((id) => categories.find((c) => c.id === id))
            .filter((c): c is MainCategoryListZipType => Boolean(c)),
        ...categories.filter((c) => !highlightedHomepageCategories.includes(c.id)),
    ].map(({ id, title, location, games }) => ({
        id,
        title,
        location,
        games: games?.map(unzipShortGame) || Array<undefined>(MAX_GAME_IN_CATEGORY).fill(undefined),
    }))
})

const topForSchema = computed(() => categoriesWithGames.value?.find((cat) => cat.id === 'trending_now'))
const topGamesForSchema = computed(() => topForSchema.value?.games)
const mainEntity = computed(() =>
    topGamesForSchema.value
        ? {
              mainEntity: {
                  '@type': 'ItemList',
                  name: topForSchema.value?.title, //TODO: get from BE full name of category with 'games' word
                  itemListOrder: 'http://schema.org/ItemListOrderAscending',
                  numberOfItems: topGamesForSchema.value.length,
                  itemListElement: topGamesForSchema.value.map(([hru, title], index) => ({
                      '@type': 'ListItem',
                      position: index + 1,
                      item: {
                          '@type': 'VideoGame',
                          name: title,
                          url: getFullLink({ name: ROUTE_NAMES.GAME_PAGE, params: { game: hru } }),
                      },
                  })),
              },
          }
        : {},
)

onServerPrefetch(suspense)

useActiveNSec(5, () => {
    loggerStore.logEvent({
        event: 'custom_event',
        eventName: 'homepage_5s',
    })
})

const pageScripts = computed(() => {
    return [
        {
            type: 'application/ld+json',
            innerHTML: JSON.stringify({
                '@context': 'https://schema.org',
                '@graph': [
                    Organization.value,
                    WebSite.value,
                    {
                        '@type': 'WebPage',
                        '@id': `${getFullLink({ name: ROUTE_NAMES.MAIN })}#WebPage`,
                        url: getFullLink({ name: ROUTE_NAMES.MAIN }),
                        name: tr('headMeta.title'),
                        description: tr('headMeta.description'),
                        inLanguage: getInLanguage(),
                        isPartOf: {
                            '@id': WebSiteId.value,
                        },
                        image: {
                            '@type': 'ImageObject',
                            url: PlaygamaOG,
                            width: '2400',
                            height: '1260',
                        },

                        ...mainEntity.value,
                    },
                ],
            }),
        },
    ]
})

useHead({ script: pageScripts })
</script>
<style module>
.page {
    display: flex;
    flex-direction: column;
    row-gap: 16px;
    column-gap: 12px;
}

.seo_override {
    border-radius: 16px;
    background: var(--carbon-300);
    padding: 16px;
    box-sizing: border-box;
}
</style>
